import { useCallback, useMemo, useState } from "react";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { trackSessionEvent } from "../../../analytics/sessionEvents";
import {
  applyEditForClarityDetections,
  detectEditForClarity,
  type EditForClarityDetection,
  type EditForClarityIntensity,
} from "../ai/editForClarity";
import {
  classifyAmbiguousFillerWords,
  type FillerWordTokenizedWord,
  type PostAiFillerWordsFn,
} from "../ai/fillerWords";
import {
  DEFAULT_ENABLED_FILLER_WORD_CATEGORY_IDS,
  FILLER_WORD_CATEGORIES,
} from "../FillerWords/fillerWord";
import {
  buildAmbiguousFillerWordRequests,
  buildLocalFillerWordCandidates,
  mergeFillerWordServerDecisions,
} from "../FillerWords/fillerWordPipeline";
import { detectRetakes, type RetakeDetection } from "../ai/retakes";

type GetTranscriptFn = (params: {
  transcriptId: string;
  projectId: string;
}) => Promise<any | undefined>;
type PostAiRetakesFn = (args: {
  projectId: string;
  maxDetections?: number;
  transcript: {
    text: string;
    tokens: Array<{
      tokenId: string;
      text: string;
      startSec: number;
      endSec: number;
      sourceIndex: number;
      speakerId?: string;
      speakerName?: string;
    }>;
  };
}) => Promise<
  | {
      detections?: Array<{
        id?: string;
        startTokenId?: string;
        endTokenId?: string;
        startSec?: number;
        endSec?: number;
        sourceIndex?: number;
        matchedText?: string;
        confidence?: number;
        reason?: string;
      }>;
    }
  | undefined
>;

type PostAiEditForClarityFn = (args: {
  projectId: string;
  intensity: EditForClarityIntensity;
  maxDetections?: number;
  transcript: {
    text: string;
    tokens: Array<{
      tokenId: string;
      text: string;
      startSec: number;
      endSec: number;
      sourceIndex: number;
      speakerId?: string;
      speakerName?: string;
    }>;
  };
}) => Promise<
  | {
      detections?: Array<{
        id?: string;
        categoryId?: string;
        startTokenId?: string;
        endTokenId?: string;
        startSec?: number;
        endSec?: number;
        sourceIndex?: number;
        matchedText?: string;
        confidence?: number;
        reason?: string;
      }>;
    }
  | undefined
>;

export type EditForClarityReviewStatus = "pending" | "accepted" | "rejected";

type UseEditForClarityReviewWorkflowArgs = {
  getTranscript: GetTranscriptFn;
  postAiRetakes?: PostAiRetakesFn;
  postAiFillerWords?: PostAiFillerWordsFn;
  postAiEditForClarity?: PostAiEditForClarityFn;
  onCloseSidebar: () => void;
};

const EPS = 1e-3;
const skipClientBaselineRetakesInEditForClarity = false;

const toFinite = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const detectionRangeIsValid = (d: EditForClarityDetection): boolean => {
  const startSec = toFinite(d?.startSec, NaN);
  const endSec = toFinite(d?.endSec, NaN);
  return (
    Number.isFinite(startSec) &&
    Number.isFinite(endSec) &&
    endSec > startSec + EPS
  );
};

const buildEditedTranscriptWordsForReview = (): FillerWordTokenizedWord[] => {
  const inputWords = Array.isArray(ScriptLayerStore.transcriptWords)
    ? (ScriptLayerStore.transcriptWords as any[])
    : [];

  const words: Array<{
    text: string;
    startSec: number;
    endSec: number;
    sourceIndex: number;
    speakerId?: string;
    speaker?: string;
  }> = [];

  for (const w of inputWords) {
    const text = String((w as any)?.text ?? "").trim();
    if (!text) continue;

    const startSec = toFinite((w as any)?.startSec, NaN);
    const endSec = toFinite((w as any)?.endSec, startSec);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec < startSec) continue;

    const sourceIndex = Number((w as any)?.sourceIndex);
    const source = Number.isFinite(sourceIndex) ? sourceIndex : 0;

    if (ScriptLayerStore.isTimeInDeleteCut(startSec)) continue;
    if (ScriptLayerStore.isTimeInGap(startSec)) continue;
    if (ScriptLayerStore.isTimeInStrikethroughCut(startSec)) continue;
    if (ScriptLayerStore.isTimeInRetakeCut(startSec)) continue;
    if (ScriptLayerStore.isTimeInTranscriptHiddenRange(startSec)) continue;

    words.push({
      text,
      startSec,
      endSec,
      sourceIndex: source,
      speakerId:
        (w as any)?.speakerId != null && String((w as any).speakerId).trim()
          ? String((w as any).speakerId).trim()
          : undefined,
      speaker:
        typeof (w as any)?.speaker === "string" && String((w as any).speaker).trim()
          ? String((w as any).speaker).trim()
          : undefined,
    });
  }

  words.sort((a, b) => {
    if (a.startSec !== b.startSec) return a.startSec - b.startSec;
    if (a.endSec !== b.endSec) return a.endSec - b.endSec;
    return a.sourceIndex - b.sourceIndex;
  });

  return words.map((word, idx) => ({
    ...word,
    tokenId: `efc_fw_${Number(word.sourceIndex ?? 0)}_${Number(word.startSec ?? 0).toFixed(6)}_${Number(
      word.endSec ?? word.startSec ?? 0,
    ).toFixed(6)}_${idx}`,
  }));
};

export const useEditForClarityReviewWorkflow = ({
  getTranscript,
  postAiRetakes,
  postAiFillerWords,
  postAiEditForClarity,
  onCloseSidebar,
}: UseEditForClarityReviewWorkflowArgs) => {
  const [isDetecting, setIsDetecting] = useState(false);
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const [draft, setDraft] = useState<EditForClarityDetection[]>([]);
  const [statusById, setStatusById] = useState<
    Record<string, EditForClarityReviewStatus>
  >({});
  const [busyById, setBusyById] = useState<Record<string, boolean>>({});
  const [appliedCutIdByDetectionId, setAppliedCutIdByDetectionId] = useState<
    Record<string, string>
  >({});
  const [activeDetectionId, setActiveDetectionId] = useState<string | null>(
    null,
  );

  const draftById = useMemo(() => {
    const map = new Map<string, EditForClarityDetection>();
    for (const d of draft) {
      const id = String(d?.id ?? "").trim();
      if (!id) continue;
      map.set(id, d);
    }
    return map;
  }, [draft]);

  const pendingCount = useMemo(() => {
    return draft.reduce((count, d) => {
      const id = String(d?.id ?? "").trim();
      if (!id) return count;
      return statusById[id] === "pending" || !statusById[id]
        ? count + 1
        : count;
    }, 0);
  }, [draft, statusById]);

  const acceptedCount = useMemo(() => {
    return draft.reduce((count, d) => {
      const id = String(d?.id ?? "").trim();
      if (!id) return count;
      return statusById[id] === "accepted" ? count + 1 : count;
    }, 0);
  }, [draft, statusById]);

  const rejectedCount = useMemo(() => {
    return draft.reduce((count, d) => {
      const id = String(d?.id ?? "").trim();
      if (!id) return count;
      return statusById[id] === "rejected" ? count + 1 : count;
    }, 0);
  }, [draft, statusById]);

  const clearReviewState = useCallback(() => {
    setError(null);
    setHasRun(false);
    setDraft([]);
    setStatusById({});
    setBusyById({});
    setAppliedCutIdByDetectionId({});
    setActiveDetectionId(null);
  }, []);

  const closeAndReset = useCallback(() => {
    clearReviewState();
    onCloseSidebar();
  }, [clearReviewState, onCloseSidebar]);

  const setBusy = useCallback((detectionId: string, busy: boolean) => {
    const id = String(detectionId ?? "").trim();
    if (!id) return;
    setBusyById((prev) => {
      const next = { ...prev };
      if (busy) next[id] = true;
      else delete next[id];
      return next;
    });
  }, []);

  const rangesOverlap = useCallback(
    (a: EditForClarityDetection, b: EditForClarityDetection): boolean => {
      if (Number(a.sourceIndex ?? 0) !== Number(b.sourceIndex ?? 0))
        return false;
      const aStart = toFinite(a.startSec, NaN);
      const aEnd = toFinite(a.endSec, NaN);
      const bStart = toFinite(b.startSec, NaN);
      const bEnd = toFinite(b.endSec, NaN);
      if (
        !Number.isFinite(aStart) ||
        !Number.isFinite(aEnd) ||
        !Number.isFinite(bStart) ||
        !Number.isFinite(bEnd)
      ) {
        return false;
      }
      return aStart <= bEnd - EPS && bStart <= aEnd - EPS;
    },
    [],
  );

  const dedupeDetections = useCallback(
    (input: EditForClarityDetection[]): EditForClarityDetection[] => {
      if (!input.length) return [];
      const byKey = new Map<string, EditForClarityDetection>();
      for (const d of input) {
        const key = `${d.categoryId}|${Number(d.startSec).toFixed(3)}|${Number(d.endSec).toFixed(3)}|${Number(
          d.sourceIndex ?? 0,
        )}`;
        const prev = byKey.get(key);
        if (!prev || Number(d.confidence ?? 0) > Number(prev.confidence ?? 0)) {
          byKey.set(key, d);
        }
      }
      return [...byKey.values()].sort((a, b) => {
        if (a.startSec !== b.startSec) return a.startSec - b.startSec;
        if (a.endSec !== b.endSec) return a.endSec - b.endSec;
        return String(a.id ?? "").localeCompare(String(b.id ?? ""));
      });
    },
    [],
  );

  const collapseOverlappingDetections = useCallback(
    (input: EditForClarityDetection[]): EditForClarityDetection[] => {
      if (!input.length) return [];

      const ordered = [...input].sort((a, b) => {
        if (a.startSec !== b.startSec) return a.startSec - b.startSec;
        if (a.endSec !== b.endSec) return a.endSec - b.endSec;
        return Number(b.confidence ?? 0) - Number(a.confidence ?? 0);
      });

      const out: EditForClarityDetection[] = [];
      for (const detection of ordered) {
        const idx = out.findIndex((existing) => {
          return (
            existing.categoryId === detection.categoryId &&
            Number(existing.sourceIndex ?? 0) === Number(detection.sourceIndex ?? 0) &&
            rangesOverlap(existing, detection)
          );
        });

        if (idx < 0) {
          out.push(detection);
          continue;
        }

        const existing = out[idx];
        const existingScore = Number(existing.confidence ?? 0);
        const nextScore = Number(detection.confidence ?? 0);
        if (nextScore > existingScore) {
          out[idx] = detection;
        }
      }

      return out;
    },
    [rangesOverlap],
  );

  const retakeMinConfidenceForIntensity = useCallback(
    (intensity: EditForClarityIntensity): number => {
      if (intensity === "low") return 0.95;
      if (intensity === "heavy") return 0.82;
      return 0.9;
    },
    [],
  );

  const fillerCategoryIdsForIntensity = useCallback(
    (intensity: EditForClarityIntensity) => {
      if (intensity === "heavy") {
        return new Set<string>(FILLER_WORD_CATEGORIES.map((category) => category.id));
      }
      return new Set<string>(DEFAULT_ENABLED_FILLER_WORD_CATEGORY_IDS);
    },
    [],
  );

  const mapRetakeToClarity = useCallback(
    (retake: RetakeDetection): EditForClarityDetection => {
      const id =
        String(retake.id ?? "").trim() ||
        `clarity_retake_${retake.startSec}_${retake.endSec}_${retake.sourceIndex}`;
      return {
        id: `clarity_retake_baseline_${id}`,
        categoryId: "retake",
        startSec: Number(retake.startSec ?? 0),
        endSec: Number(retake.endSec ?? 0),
        sourceIndex: Number(retake.sourceIndex ?? 0),
        confidence: Number(retake.confidence ?? 0),
        matchedText: String(retake.removedText ?? "").trim() || "",
        reason:
          String(retake.reason ?? "").trim() || "Detected by retake baseline.",
      };
    },
    [],
  );

  const seekToDetection = useCallback(
    (detectionId: string) => {
      const id = String(detectionId ?? "").trim();
      if (!id) return;
      const detection = draftById.get(id);
      if (!detection) return;

      const sourceStartSec = toFinite(detection.startSec, 0);
      const compTime =
        ScriptLayerStore.mapSourceTimeToCompositionTime(sourceStartSec);
      const seekTime =
        compTime != null && Number.isFinite(compTime)
          ? compTime
          : sourceStartSec;
      void CompositionService.seek(seekTime);
      setActiveDetectionId(id);
    },
    [draftById],
  );

  const applySingleDetection = useCallback(
    async (detection: EditForClarityDetection): Promise<string | null> => {
      if (!detectionRangeIsValid(detection)) return null;
      const startSec = Number(detection.startSec);
      const endSec = Number(detection.endSec);
      const sourceIndex = toFinite(detection.sourceIndex, 0);

      try {
        const cut = await CompositionService.cutScriptLayerRangeWithAnalysis(
          startSec,
          endSec,
          sourceIndex,
          {
            transcriptMode:
              detection.categoryId === "retake"
                ? "retake-strikethrough"
                : "strikethrough",
          },
        );
        const cutId = String((cut as any)?.id ?? "").trim();
        return cutId || null;
      } catch {
        const cut = CompositionService.cutScriptLayerRange(startSec, endSec, {
          transcriptMode:
            detection.categoryId === "retake"
              ? "retake-strikethrough"
              : "strikethrough",
        });
        const cutId = String((cut as any)?.id ?? "").trim();
        return cutId || null;
      }
    },
    [],
  );

  const acceptDetection = useCallback(
    async (detectionId: string) => {
      const id = String(detectionId ?? "").trim();
      if (!id || isApplyingAll || isDetecting || busyById[id]) return;
      const detection = draftById.get(id);
      if (!detection) return;
      if (statusById[id] === "accepted") {
        setActiveDetectionId(id);
        return;
      }

      setError(null);
      setBusy(id, true);
      try {
        trackSessionEvent("edit_for_clarity");
        const cutId = await applySingleDetection(detection);
        setStatusById((prev) => ({ ...prev, [id]: "accepted" }));
        if (cutId) {
          setAppliedCutIdByDetectionId((prev) => ({ ...prev, [id]: cutId }));
        }
        setActiveDetectionId(id);
      } catch (e: any) {
        const msg = String(e?.message ?? e ?? "Failed to apply clarity edit.");
        setError(msg);
      } finally {
        setBusy(id, false);
      }
    },
    [
      applySingleDetection,
      busyById,
      draftById,
      isApplyingAll,
      isDetecting,
      setBusy,
      statusById,
    ],
  );

  const rejectDetection = useCallback(
    async (detectionId: string) => {
      const id = String(detectionId ?? "").trim();
      if (!id || isApplyingAll || isDetecting || busyById[id]) return;

      setError(null);
      setBusy(id, true);
      try {
        const existingCutId = String(
          appliedCutIdByDetectionId[id] ?? "",
        ).trim();
        if (existingCutId) {
          CompositionService.restoreScriptCut(existingCutId);
        }

        setAppliedCutIdByDetectionId((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setStatusById((prev) => ({ ...prev, [id]: "rejected" }));
        setActiveDetectionId(id);
      } catch (e: any) {
        const msg = String(e?.message ?? e ?? "Failed to omit clarity edit.");
        setError(msg);
      } finally {
        setBusy(id, false);
      }
    },
    [appliedCutIdByDetectionId, busyById, isApplyingAll, isDetecting, setBusy],
  );

  const detect = useCallback(
    async (intensity: EditForClarityIntensity) => {
      if (isDetecting || isApplyingAll) return;
      setIsDetecting(true);
      setError(null);

      try {
        if (!CompositionService.isInitialized()) {
          throw new Error("Composition is not ready yet.");
        }
        const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
        if (!projectId) {
          throw new Error("Missing project id.");
        }
        const normalizedIntensity: EditForClarityIntensity =
          intensity === "low" || intensity === "medium" || intensity === "heavy"
            ? intensity
            : "medium";
        const shouldFetchBaselineRetakes =
          postAiRetakes && !skipClientBaselineRetakesInEditForClarity;
        const retakesPromise = shouldFetchBaselineRetakes
          ? detectRetakes({
              getTranscript,
              postAiRetakes,
              projectId,
              minConfidence:
                retakeMinConfidenceForIntensity(normalizedIntensity),
              maxGapSec: 10,
              maxCandidates: 40,
              useAudioVeto: true,
            })
          : Promise.resolve(undefined);

        const clarityPromise = detectEditForClarity({
          projectId,
          intensity: normalizedIntensity,
          postAiEditForClarity,
          maxDetections: 1600,
          minConfidence: 0,
        });

        const fillerWords = buildEditedTranscriptWordsForReview();
        const enabledFillerCategoryIds =
          fillerCategoryIdsForIntensity(normalizedIntensity);
        const fillerLocalCandidates =
          fillerWords.length > 0 && enabledFillerCategoryIds.size > 0
            ? buildLocalFillerWordCandidates(
                fillerWords,
                enabledFillerCategoryIds,
              )
            : [];
        const provisionalFillerCandidates = fillerLocalCandidates.filter(
          (candidate) => candidate.decision !== "auto_keep",
        );
        const fillerMatchesByCategory = new Map<string, number>();
        for (const candidate of provisionalFillerCandidates) {
          fillerMatchesByCategory.set(
            candidate.categoryId,
            (fillerMatchesByCategory.get(candidate.categoryId) ?? 0) + 1,
          );
        }
        const ambiguousFillerCandidates =
          buildAmbiguousFillerWordRequests(fillerLocalCandidates);
        console.log("[EditForClarity] filler words local baseline", {
          intensity: normalizedIntensity,
          candidateIds: [...enabledFillerCategoryIds],
          wordCount: fillerWords.length,
          localMatchCount: provisionalFillerCandidates.length,
          localMatchesByCategory: Object.fromEntries(
            fillerMatchesByCategory.entries(),
          ),
          autoRemoveCount: fillerLocalCandidates.filter(
            (candidate) => candidate.decision === "auto_remove",
          ).length,
          autoKeepCount: fillerLocalCandidates.filter(
            (candidate) => candidate.decision === "auto_keep",
          ).length,
          ambiguousCount: ambiguousFillerCandidates.length,
          localMatchSamples: provisionalFillerCandidates
            .slice(0, 12)
            .map((candidate) => candidate.matchedText),
        });

        const fillerDecisionsPromise =
          postAiFillerWords && ambiguousFillerCandidates.length > 0
            ? classifyAmbiguousFillerWords({
                projectId,
                candidates: ambiguousFillerCandidates,
                postAiFillerWords,
                maxDetections: 1600,
              })
            : Promise.resolve([]);

        const [retakeRes, fillerDecisions, res] = await Promise.all([
          retakesPromise,
          fillerDecisionsPromise,
          clarityPromise,
        ]);
        const baselineRetakes: EditForClarityDetection[] = collapseOverlappingDetections(
          shouldFetchBaselineRetakes
            ? (Array.isArray(retakeRes?.detections) ? retakeRes.detections : [])
                .map(mapRetakeToClarity)
                .filter((d) => detectionRangeIsValid(d))
            : [],
        );

        console.log("[EditForClarity] filler words raw detections", {
          intensity: normalizedIntensity,
          candidateIds: [...enabledFillerCategoryIds],
          wordCount: fillerWords.length,
          rawDetectionCount: fillerDecisions.length,
          detections: fillerDecisions,
        });

        const fillerDecisionById = new Map(
          fillerDecisions.map((decision) => [
            String(decision?.id ?? "").trim(),
            decision,
          ]),
        );
        const mergedFillerCandidates =
          postAiFillerWords && ambiguousFillerCandidates.length > 0
            ? mergeFillerWordServerDecisions(
                fillerLocalCandidates,
                fillerDecisions,
              )
            : provisionalFillerCandidates;
        const mappedFillerDetections: EditForClarityDetection[] = [];
        for (const candidate of mergedFillerCandidates) {
          const startSec = Number(candidate.startSec ?? NaN);
          const endSec = Number(candidate.endSec ?? NaN);
          if (
            !Number.isFinite(startSec) ||
            !Number.isFinite(endSec) ||
            endSec <= startSec + EPS
          ) {
            continue;
          }

          const serverDecision = fillerDecisionById.get(candidate.id);
          const detection: EditForClarityDetection = {
            id: candidate.id,
            categoryId: "filler_disfluency",
            startSec,
            endSec,
            sourceIndex: Number(candidate.sourceIndex ?? 0),
            confidence:
              candidate.decision === "auto_remove"
                ? 0.99
                : Math.max(
                    0,
                    Math.min(1, toFinite(serverDecision?.confidence, 0.9)),
                  ),
            matchedText: candidate.matchedText,
            reason:
              String(serverDecision?.reason ?? "").trim() ||
              candidate.decisionReason,
          };
          if (detectionRangeIsValid(detection)) {
            mappedFillerDetections.push(detection);
          }
        }
        const fillerDetections = collapseOverlappingDetections(
          mappedFillerDetections,
        );
        console.log("[EditForClarity] filler words mapped detections", {
          intensity: normalizedIntensity,
          mappedDetectionCount: fillerDetections.length,
          detections: fillerDetections,
        });
        const clarityDetections = collapseOverlappingDetections((
          Array.isArray(res?.detections) ? res.detections : []
        ).filter(
          (d) =>
            detectionRangeIsValid(d) &&
            d.categoryId !== "filler_disfluency" &&
            d.categoryId !== "retake",
        ));

        const merged: EditForClarityDetection[] = [...baselineRetakes];
        for (const row of fillerDetections) {
          if (merged.some((existing) => rangesOverlap(existing, row))) continue;
          merged.push(row);
        }
        for (const row of clarityDetections) {
          if (merged.some((existing) => rangesOverlap(existing, row))) continue;
          merged.push(row);
        }

        const detections = dedupeDetections(merged);
        setDraft(detections);
        setStatusById({});
        setBusyById({});
        setAppliedCutIdByDetectionId({});
        setActiveDetectionId(detections[0]?.id ?? null);
      } catch (e: any) {
        const msg = String(
          e?.message ?? e ?? "Failed to detect edit-for-clarity suggestions.",
        );
        setError(msg);
      } finally {
        setHasRun(true);
        setIsDetecting(false);
      }
    },
    [
      collapseOverlappingDetections,
      dedupeDetections,
      fillerCategoryIdsForIntensity,
      getTranscript,
      isApplyingAll,
      isDetecting,
      mapRetakeToClarity,
      postAiEditForClarity,
      postAiRetakes,
      rangesOverlap,
      retakeMinConfidenceForIntensity,
    ],
  );

  const acceptAllRemaining = useCallback(async () => {
    if (isApplyingAll || isDetecting) return;

    const remaining = draft.filter((d) => {
      const id = String(d?.id ?? "").trim();
      if (!id) return false;
      const status = statusById[id] ?? "pending";
      return status === "pending";
    });

    if (!remaining.length) {
      closeAndReset();
      return;
    }

    setIsApplyingAll(true);
    setError(null);
    try {
      trackSessionEvent("edit_for_clarity");
      await applyEditForClarityDetections({
        detections: remaining,
      });
      closeAndReset();
    } catch (e: any) {
      const msg = String(e?.message ?? e ?? "Failed to apply clarity edits.");
      setError(msg);
    } finally {
      setIsApplyingAll(false);
    }
  }, [closeAndReset, draft, isApplyingAll, isDetecting, statusById]);

  return {
    isDetecting,
    isApplyingAll,
    isLoading: isDetecting || isApplyingAll,
    error,
    hasRun,
    draft,
    statusById,
    busyById,
    activeDetectionId,
    pendingCount,
    acceptedCount,
    rejectedCount,
    canAcceptAllRemaining: pendingCount > 0,
    detect,
    seekToDetection,
    acceptDetection,
    rejectDetection,
    acceptAllRemaining,
    reset: clearReviewState,
    done: closeAndReset,
    resetAndClose: closeAndReset,
  };
};

export type EditForClarityReviewWorkflow = ReturnType<
  typeof useEditForClarityReviewWorkflow
>;
